import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { DateRangePicker } from "react-date-range";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import { FaCalendarAlt, FaFileExport, FaTags, FaMoneyBillWave } from "react-icons/fa";

import SideNav from "../components/SideNav";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./ReportsPage.css";
import useLicenseCheck from "../hooks/useLicenseCheck";

const formatCurrency = (amount) => {
  const numericAmount = Number(amount) || 0;
  return `GH₵ ${numericAmount.toFixed(2)}`;
};

const ReportsPage = () => {
  useLicenseCheck();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
      endDate: new Date(),
      key: "selection"
    }
  ]);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/pharmacy/api";

  // Fetch reports whenever the date range changes
  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication token missing. Please login again.");
        setLoading(false);
        return;
      }

      try {
        const startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
        const endDate = format(dateRange[0].endDate, "yyyy-MM-dd");

        const response = await axios.get(`${API_BASE_URL}/get_sales_report.php`, {
          params: { start_date: startDate, end_date: endDate },
          headers: { Authorization: `Bearer ${token}` }
        });

        // ✅ License active & data available
        if (response.data.status === "success" && Array.isArray(response.data.data)) {
          setReports(response.data.data);
          setError("");
        } 
        // ❌ License expired
        else if (response.data.status === "expired") {
          localStorage.removeItem("token");
          localStorage.removeItem("rememberEmail");
          localStorage.removeItem("rememberPassword");
          window.location.href = response.data.redirect || "/renew-license";
          return;
        } 
        // ⚠️ Other backend error
        else {
          setReports([]);
          setError(response.data.message || "Failed to fetch reports.");
        }
      } catch (err) {
        console.error("Error fetching reports:", err);
        // Handle expired / invalid token from backend
        const backendMsg = err.response?.data?.message || "";
        if (backendMsg.toLowerCase().includes("expired") || backendMsg.toLowerCase().includes("renew") || backendMsg.toLowerCase().includes("invalid token")) {
          localStorage.removeItem("token");
          localStorage.removeItem("rememberEmail");
          localStorage.removeItem("rememberPassword");
          navigate(response.data.redirect || "/renew-license"); // ✅ use navigate
        } else {
          setError("Failed to fetch reports. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [dateRange, API_BASE_URL]);

  // Summary data
  const summaryData = useMemo(() => {
    if (!reports.length) return { totalRevenue: 0, totalItemsSold: 0 };

    const totalRevenue = reports.reduce((sum, item) => sum + parseFloat(item.total_sales), 0);
    const totalItemsSold = reports.reduce((sum, item) => sum + parseInt(item.total_quantity_sold, 10), 0);

    return { totalRevenue, totalItemsSold };
  }, [reports]);

  // Top 5 products for chart
  const chartData = useMemo(() => {
    return reports
      .slice()
      .sort((a, b) => parseFloat(b.total_sales) - parseFloat(a.total_sales))
      .slice(0, 5)
      .map((item) => ({
        name: item.product_name,
        Sales: parseFloat(item.total_sales)
      }));
  }, [reports]);

  // CSV Export
  const handleExport = () => {
    if (reports.length === 0) {
      alert("There is no data to export.");
      return;
    }

    const headers = ["Product Name", "Total Quantity Sold", "Total Sales (GH₵)"];
    const rows = reports.map((report) => [
      `"${report.product_name.replace(/"/g, '""')}"`,
      report.total_quantity_sold,
      parseFloat(report.total_sales).toFixed(2)
    ]);

    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
    const endDate = format(dateRange[0].endDate, "yyyy-MM-dd");
    link.setAttribute("download", `sales_report_${startDate}_to_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".date-picker-btn") &&
        !event.target.closest(".date-picker-container")
      ) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="reports-page-container">
      <SideNav activePage="Reports" />
      <main className="reports-main-content">
        <header className="page-header">
          <h1>Sales Report</h1>
          <div className="header-actions" style={{ position: "relative" }}>
            <button className="date-picker-btn" onClick={() => setShowDatePicker(!showDatePicker)}>
              <FaCalendarAlt />
              <span>
                {`${format(dateRange[0].startDate, "MMM d, yyyy")} - ${format(dateRange[0].endDate, "MMM d, yyyy")}`}
              </span>
            </button>

            {showDatePicker && (
              <div className="date-picker-container">
                <DateRangePicker
                  onChange={(item) => setDateRange([item.selection])}
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                  months={1}
                  ranges={dateRange}
                  direction="horizontal"
                />
              </div>
            )}

            <button className="export-btn" onClick={handleExport} disabled={reports.length === 0 || loading}>
              <FaFileExport />
              <span>Export</span>
            </button>
          </div>
        </header>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-icon revenue-icon">
              <FaMoneyBillWave size={24} />
            </div>
            <div className="stat-card-info">
              <p>Total Revenue</p>
              <h3>{formatCurrency(summaryData.totalRevenue)}</h3>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-card-icon items-icon">
              <FaTags size={24} />
            </div>
            <div className="stat-card-info">
              <p>Total Items Sold</p>
              <h3>{summaryData.totalItemsSold}</h3>
            </div>
          </div>
        </div>

        <div className="content-card">
          {loading ? (
            <p className="centered-message">Loading report data...</p>
          ) : error ? (
            <p className="centered-message error-message">{error}</p>
          ) : reports.length === 0 ? (
            <p className="centered-message">No sales data found for the selected period.</p>
          ) : (
            <>
              <div className="chart-container">
                <h2>Top 5 Selling Products by Revenue</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="Sales" fill="#2dd4bf" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="table-container">
                <h2>Detailed Product Sales</h2>
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th className="text-center">Total Quantity Sold</th>
                      <th className="text-right">Total Sales</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report, index) => (
                      <tr key={index}>
                        <td>{report.product_name}</td>
                        <td className="text-center">{report.total_quantity_sold}</td>
                        <td className="text-right">{formatCurrency(report.total_sales)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ReportsPage;